import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { Alert, Button, CircularProgress, Dialog, DialogContent } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { unwoundRowMetadataFromId } from 'interfaces/genericFields/unwindRowsWithInnerArrays';

import { DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { useTableEditingContext } from 'components/atoms/EditableDataGrid/TableEditingContext';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import ConfirmationModal from 'components/modals/ConfirmationModal';
import { has } from 'es-toolkit/compat';
import { Permission } from 'interfaces/permissionOption';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { Slide } from 'interfaces/slide';
import { concat, filter, find, first, flatMap, includes, isEmpty, map, size, some } from 'lodash';
import React, { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { DelimitedArrayParam, StringParam, useQueryParam } from 'use-query-params';
import { CompressedJsonParam } from 'utils/customQueryParamsConfigs';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { DEFAULT_PAGE_SIZE } from 'utils/useEncodedFilters/constants';
import { usePermissions } from 'utils/usePermissions';
import { useEditableFields } from '../ProceduresPage/DataGrids/useEditableFields';
import { canQueryPendingSlides } from '../ProceduresPage/pendingSlidesPermissions';
import { useAllPendingSlides } from '../ProceduresPage/usePendingSlides';
import {
  CaseManifestEntryChoices,
  CaseManifestHandling,
  CaseManifestSlideSourceMenu,
  ManifestWorkspaceSurface,
} from './CaseManifestControls';
import { CaseManifestDialogFooter, CaseManifestPlanToolbar } from './CaseManifestDialogChrome';
import { CaseManifestFileStatus } from './CaseManifestFileActions';
import { CaseManifestTargetCaseCell } from './CaseManifestTargetCaseCell';
import { CaseManifestWorkspace } from './CaseManifestWorkspace';
import {
  buildCaseOrganizationPlan,
  CaseOrganizationExecutionMode,
  caseOrganizationHandlingLabels,
} from './caseOrganizationPlan';
import { updateManifestCaseTarget } from './caseTargetAssignment';
import { CaseOrganizationConfirmationDialog } from './ConfirmationDialog';
import {
  getDefaultManifestDialogUrlState,
  ManifestCaseTarget,
  ManifestDialogUrlState,
  ManifestSurface,
  ManifestSurfaceParam,
} from './dialogState';
import { StudyManifestInProgressDialog } from './InProgressDialog';
import { resolveManifestDraftCases as resolveDraftCases, resolveManifestDraftAssignments } from './manifestDraft';
import { ManifestDraftStatus } from './ManifestDraftStatus';
import {
  collectCaseSharedMetadataErrors,
  collectExplicitNewCaseLabelErrors,
  collectManifestCaseLabelErrors,
  collectManifestValidationErrors,
  getManifestCaseTargets,
  removeSlidesFromManifestCases,
  resolveManifestWorkflow,
} from './manifestResolution';
import { projectManifestWorkbookPlan } from './manifestWorkbook';
import { useAllStudyProcedures } from './useAllStudyProcedures';
import { useCaseOrganizationExecution } from './useCaseOrganizationExecution';
import { useManifestFileImport } from './useManifestFileImport';
import { useManifestPlanEditing } from './useManifestPlanEditing';
import { useManifestSlideMetadata } from './useManifestSlideMetadata';
import { useManifestSourceSelection } from './useManifestSourceSelection';
import { useManifestWorkbookExport } from './useManifestWorkbookExport';

interface StudyManifestDialogProps {
  studyId: string;
  onClose: (event?: any) => void;
  onOpenStandard?: () => void;
  open: boolean;
}

const dialogTitle = 'Organize slides';
const crossLabSearchSurface = 'Cross Lab Search';
type WorkspaceSurface = ManifestWorkspaceSurface;

const CaseManifestDialog: FunctionComponent<React.PropsWithChildren<StudyManifestDialogProps>> = ({
  studyId,
  onClose,
  open,
}) => {
  const confirmWithModal = useConfirmation();
  const { labId, currentLab } = useCurrentLabId();
  const { hasEveryPermission, hasPermission } = usePermissions();
  const canReadStudySource = canQueryPendingSlides({ hasPermission, studyId });
  const canReadLabSource = hasPermission(Permission.ViewPendingSlides);
  const canIncludeAssigned = hasPermission(Permission.ApplyCaseManifest);
  const canSwitchLabs = hasPermission(Permission.ApplyPermissionsAcrossLabs);
  const canAddSlidesAcrossLabs = hasEveryPermission([
    Permission.ApplyCaseManifest,
    Permission.ApplyPermissionsAcrossLabs,
  ]);
  const { fieldsContext: procedureFieldContext } = useTableEditingContext<Procedure, ProceduresFieldsContext, Slide>();

  const [manifestDialogUrlState, setManifestDialogUrlState] = useQueryParam<ManifestDialogUrlState | undefined>(
    'caseManifestState',
    CompressedJsonParam
  );
  const [caseManifestSelectionMode, setCaseManifestSelectionMode] = useQueryParam(
    'caseManifestSelectionMode',
    StringParam
  );
  const [, setCaseManifestSelectedRows] = useQueryParam('caseManifestSelectedRows', DelimitedArrayParam);
  const [, setCaseManifestOmittedRows] = useQueryParam('caseManifestOmittedRows', DelimitedArrayParam);
  const [manifestSurfaceState, setManifestSurface] = useQueryParam<ManifestSurface | undefined>(
    'manifestSurface',
    ManifestSurfaceParam
  );

  const manifestState = useMemo(
    () => ({ ...getDefaultManifestDialogUrlState(), ...(manifestDialogUrlState || {}) }),
    [manifestDialogUrlState]
  );
  const {
    uploadedManifestCases = [],
    uploadedNotIngestedSlides = [],
    uploadedWarningMessages = [],
    uploadedErrorMessages = [],
    manualManifestCases = [],
    manualManifestSlideIdsToLoad = [],
    manualManifestGroupIntoSingleCase = false,
    sourceAssignmentStrategy = 'oneCasePerSlide',
    existingCaseAssignmentsBySlideId = {},
    manualManifestErrorMessages = [],
    removedSlideIds = [],
    casePatchesById = {},
    slidePatchesById = {},
    executionMode: restoredExecutionMode,
    showOnlyErrors = false,
    page = 1,
    rowsPerPage = DEFAULT_PAGE_SIZE,
    pendingSlidesSelectionAction,
  } = manifestState;

  const executionMode =
    restoredExecutionMode === undefined
      ? manifestState.overrideCases
        ? 'selectedCaseOverride'
        : 'routine'
      : restoredExecutionMode;
  const isExecutionModeValid = has(caseOrganizationHandlingLabels, [executionMode]);
  const overrideCases = isExecutionModeValid && executionMode !== 'routine';

  const updateManifestDialogUrlState = useCallback(
    (
      updates: Partial<ManifestDialogUrlState> | ((previousState: ManifestDialogUrlState) => ManifestDialogUrlState)
    ) => {
      setManifestDialogUrlState((previousState) => {
        const currentState = { ...getDefaultManifestDialogUrlState(), ...(previousState || {}) };
        return typeof updates === 'function' ? updates(currentState) : { ...currentState, ...updates };
      }, 'replaceIn');
    },
    [setManifestDialogUrlState]
  );

  const draftUploadScope = JSON.stringify([
    uploadedManifestCases,
    manualManifestCases,
    manualManifestSlideIdsToLoad,
    casePatchesById,
    slidePatchesById,
    removedSlideIds,
    existingCaseAssignmentsBySlideId,
  ]);
  const [caseOrganizationReviewOpen, setCaseOrganizationReviewOpen] = useState(false);
  const [hasChosenEntryIntent, setHasChosenEntryIntent] = useState(false);
  const [isHandlingExpanded, setIsHandlingExpanded] = useState<boolean | undefined>(undefined);
  const [selectSlidesAnchorEl, setSelectSlidesAnchorEl] = useState<HTMLElement | null>(null);
  const [localWorkspaceSurface, setLocalWorkspaceSurface] = useState<WorkspaceSurface | null>(null);
  const { fetchSlidesByIdBatches, isHydratingManualManifestSlides } = useManifestSlideMetadata({
    labId,
    manualManifestGroupIntoSingleCase,
    manualManifestSlideIdsToLoad,
    open:
      open &&
      (hasChosenEntryIntent ||
        Boolean(manifestSurfaceState) ||
        !isEmpty(manualManifestCases) ||
        !isEmpty(uploadedManifestCases)),
    updateManifestDialogUrlState,
  });

  const {
    procedures: existingProcedures,
    isLoading: isCasesLoading,
    isError: isCasesError,
  } = useAllStudyProcedures(studyId, open);

  const pendingSlidesEnabled = open && !isHydratingManualManifestSlides;
  const {
    allPendingSlide: allLabPendingSlides,
    allMockCasesOfPendingSlides,
    isPendingSlidesLoading: isLabPendingSlidesLoading,
    pendingSlidesError: labPendingSlidesError,
  } = useAllPendingSlides({ enabled: pendingSlidesEnabled, scope: 'lab' });
  const {
    allMockCasesOfPendingSlides: allMockCasesOfStudyPendingSlides,
    isPendingSlidesLoading: isStudyPendingSlidesLoading,
    pendingSlidesError: studyPendingSlidesError,
  } = useAllPendingSlides({
    enabled: pendingSlidesEnabled,
    scope: 'study',
    studyId,
  });
  const isPendingSlidesLoading = isLabPendingSlidesLoading || isStudyPendingSlidesLoading;

  const editableExistingAndMockCases = useMemo(
    () => concat(allMockCasesOfPendingSlides || [], existingProcedures || []),
    [allMockCasesOfPendingSlides, existingProcedures]
  );

  const workflowResolution = useMemo(
    () =>
      resolveManifestWorkflow({
        existingProcedures,
        manualManifestCases,
        uploadedManifestCases,
        uploadedNotIngestedSlides,
        uploadedWarningMessages,
        uploadedErrorMessages,
        overrideCases,
      }),
    [
      existingProcedures,
      manualManifestCases,
      uploadedManifestCases,
      uploadedNotIngestedSlides,
      uploadedWarningMessages,
      uploadedErrorMessages,
      overrideCases,
    ]
  );

  const resolveManifestDraftCases = useCallback(
    (manifestCases: Procedure[], targetsBySlideId?: Record<string, ManifestCaseTarget>) =>
      resolveDraftCases({ manifestCases, existingProcedures, overrideCases, targetsBySlideId }),
    [existingProcedures, overrideCases]
  );

  const baseManifestCases = useMemo(
    () => removeSlidesFromManifestCases(concat(uploadedManifestCases, manualManifestCases), removedSlideIds),
    [uploadedManifestCases, manualManifestCases, removedSlideIds]
  );

  const baseResolvedManifestCases = useMemo(
    () => resolveManifestDraftCases(baseManifestCases),
    [baseManifestCases, resolveManifestDraftCases]
  );

  const manifestCaseTargets = useMemo(
    () =>
      getManifestCaseTargets({
        cases: baseManifestCases,
        existingProcedures,
        assignmentsBySlideId: existingCaseAssignmentsBySlideId,
      }),
    [baseManifestCases, existingProcedures, existingCaseAssignmentsBySlideId]
  );

  const existingCaseAssignmentResolution = useMemo(
    () =>
      resolveManifestDraftAssignments({
        cases: baseManifestCases,
        uploadedManifestCases,
        existingProcedures,
        assignmentsBySlideId: manifestCaseTargets,
        casePatchesById,
        slidePatchesById,
        labId,
        studyId,
      }),
    [
      baseManifestCases,
      uploadedManifestCases,
      casePatchesById,
      slidePatchesById,
      existingProcedures,
      manifestCaseTargets,
      labId,
      studyId,
    ]
  );
  const currentDraftCases = useMemo(
    () =>
      existingCaseAssignmentResolution.missingTargetCount
        ? existingCaseAssignmentResolution.cases
        : resolveManifestDraftCases(existingCaseAssignmentResolution.cases, manifestCaseTargets),
    [existingCaseAssignmentResolution, manifestCaseTargets, resolveManifestDraftCases]
  );
  const isCaseLabelEditable = useCallback(
    (slideId: string) => manifestCaseTargets[slideId] === 'new',
    [manifestCaseTargets]
  );
  const [isSelectSlidesMenuOpen, setIsSelectSlidesMenuOpen] = useState(false);
  const isChoosingEntryIntent =
    !hasChosenEntryIntent && !manifestSurfaceState && isEmpty(currentDraftCases) && isEmpty(uploadedNotIngestedSlides);

  const caseOrganizationPlan = useMemo(
    () =>
      buildCaseOrganizationPlan({
        canonicalDraft: currentDraftCases,
        existingCases: existingProcedures,
        executionMode: isExecutionModeValid ? executionMode : 'routine',
        targetLabId: labId,
        targetStudyId: studyId,
      }),
    [currentDraftCases, existingProcedures, labId, executionMode, isExecutionModeValid, studyId]
  );
  const {
    applyCaseOrganization,
    completedBatchCount,
    createProceduresIsLoading,
    failedCaseBatchCount,
    failedSchedulingCount,
    overrideFailed,
    resetExecutionState,
    retryFailedCaseBatches,
    runningDialogOpen,
    setRunningDialogOpen,
    totalBatches,
  } = useCaseOrganizationExecution({ caseOrganizationPlan, labId, studyId });

  const { slidesFields: fields } = useEditableFields();
  const {
    acceptResolvedManifestUpload,
    activeDestinationConfirmation,
    activePendingOverwrite,
    activeWorkbookContext,
    closeFileMenuRef,
    currentPlanSignature,
    destinationError,
    destinationLookup,
    destinationSourcesLoading,
    destinationTransfer,
    disableInput,
    dismissDestinationConfirmation,
    dismissFileImport,
    exportedPlanSignature,
    foreignContext,
    handleFileChange,
    importManifestFile,
    invalidateUpload,
    isResolvingUploadedManifest,
    isReturningToOriginal,
    manifestInputRef,
    overwriteAccepted,
    pendingCasePlanFix,
    reconcileWorkbookContext,
    returnToOriginalPlan,
    setOverwriteAccepted,
    switchManifestDestination,
    uploadScope,
  } = useManifestFileImport({
    open,
    labId,
    studyId,
    executionMode,
    canIncludeAssigned,
    canSwitchLabs,
    draftUploadScope,
    uploadedErrorMessages,
    sourcesLoading:
      isCasesLoading ||
      isHydratingManualManifestSlides ||
      isPendingSlidesLoading ||
      Boolean(procedureFieldContext?.uiSettingsLoading),
    hasSourceError: Boolean(isCasesError || labPendingSlidesError || studyPendingSlidesError),
    existingProcedures,
    editableExistingAndMockCases,
    pendingCaseCount: size(allMockCasesOfPendingSlides),
    procedureFieldContext,
    fields,
    rowsPerPage,
    fetchSlidesByIdBatches,
    updateManifestDialogUrlState,
    onDraftAccepted: () => {
      setLocalWorkspaceSurface(null);
      setManifestSurface('Manifest');
    },
    onBeforeHandlingChange: () => setCaseOrganizationReviewOpen(false),
  });

  const onCloseDialog = (event?: any) => {
    onClose(event);
    setManifestDialogUrlState(undefined, 'replaceIn');
    setManifestSurface(undefined, 'replaceIn');
    if (caseManifestSelectionMode) {
      setCaseManifestSelectionMode(undefined, 'replaceIn');
    }
    setCaseManifestSelectedRows(undefined, 'replaceIn');
    setCaseManifestOmittedRows(undefined, 'replaceIn');
    resetExecutionState();
    if (manifestInputRef.current) {
      manifestInputRef.current.value = '';
    }
  };

  const pendingSlidesThatNotInTheManifest = useMemo(() => {
    return filter(
      allMockCasesOfPendingSlides,
      (mockCase) =>
        !find(currentDraftCases, (updatesCase) => some(updatesCase.slides, { id: first(mockCase.slides)?.id }))
    );
  }, [allMockCasesOfPendingSlides, currentDraftCases]);

  const studyScopedPendingSlidesThatNotInTheManifest = useMemo(
    () =>
      filter(
        allMockCasesOfStudyPendingSlides,
        (mockCase) =>
          !find(currentDraftCases, (updatesCase) => some(updatesCase.slides, { id: first(mockCase.slides)?.id }))
      ),
    [allMockCasesOfStudyPendingSlides, currentDraftCases]
  );

  const notIngestedSlides = workflowResolution.notIngestedSlides;
  const warningMessages = workflowResolution.warningMessages;

  const notIngestedSlidesCount = flatMap(notIngestedSlides, 'slides').length;
  const studyScopedPendingSlidesCount = studyScopedPendingSlidesThatNotInTheManifest.length;
  const selectionSourceEntries = useMemo(
    () =>
      [
        canAddSlidesAcrossLabs
          ? {
              id: crossLabSearchSurface as WorkspaceSurface,
              label: 'Find slides from other labs',
            }
          : null,
        canReadStudySource && (canIncludeAssigned || isPendingSlidesLoading || studyScopedPendingSlidesCount > 0)
          ? {
              id: 'Study Lost and Found Slides' as WorkspaceSurface,
              label: 'Study lost and found slides',
            }
          : null,
        canReadLabSource &&
        (canIncludeAssigned ||
          (!isPendingSlidesLoading &&
            pendingSlidesThatNotInTheManifest.length > 0 &&
            studyScopedPendingSlidesCount !== pendingSlidesThatNotInTheManifest.length))
          ? {
              id: 'Lab Lost and Found Slides' as WorkspaceSurface,
              label: 'Lab lost and found slides',
            }
          : null,
      ].filter(Boolean) as { id: WorkspaceSurface; label: string }[],
    [
      canAddSlidesAcrossLabs,
      canReadStudySource,
      canReadLabSource,
      canIncludeAssigned,
      isPendingSlidesLoading,
      pendingSlidesThatNotInTheManifest.length,
      studyScopedPendingSlidesCount,
    ]
  );
  const visibleSurfaces = [
    ...map(selectionSourceEntries, 'id'),
    ...(notIngestedSlidesCount > 0 ? (['Not Ingested Slides'] as ManifestSurface[]) : []),
    'Manifest' as ManifestSurface,
  ];
  const defaultAddSlidesSurface: ManifestSurface = !isEmpty(studyScopedPendingSlidesThatNotInTheManifest)
    ? 'Study Lost and Found Slides'
    : !isEmpty(pendingSlidesThatNotInTheManifest)
    ? 'Lab Lost and Found Slides'
    : 'Manifest';
  const effectiveManifestSurface =
    manifestSurfaceState === 'Manifest'
      ? 'Manifest'
      : includes(visibleSurfaces, manifestSurfaceState as ManifestSurface)
      ? (manifestSurfaceState as ManifestSurface)
      : manifestSurfaceState
      ? defaultAddSlidesSurface
      : 'Manifest';
  const activeWorkspaceSurface = localWorkspaceSurface || effectiveManifestSurface;
  const isOrganizationView = activeWorkspaceSurface === 'Manifest';
  const selectSlidesButtonLabel = isEmpty(currentDraftCases) ? 'Select slides' : 'Select more slides';
  const sourceScope = activeWorkspaceSurface === 'Study Lost and Found Slides' ? 'study' : 'lab';
  const canReadActiveSource = sourceScope === 'study' ? canReadStudySource : canReadLabSource;
  const activePendingSlidesError = sourceScope === 'study' ? studyPendingSlidesError : labPendingSlidesError;
  const pendingSlidesCasesToDisplay =
    activeWorkspaceSurface === 'Study Lost and Found Slides'
      ? studyScopedPendingSlidesThatNotInTheManifest
      : pendingSlidesThatNotInTheManifest;
  const { addDirectSlidesToManifest, sourcePanelProps } = useManifestSourceSelection({
    open,
    labId,
    studyId,
    activeWorkspaceSurface,
    currentDraftCases,
    pendingSlidesCasesToDisplay,
    sourceScope,
    canReadActiveSource,
    canReadStudySource,
    canIncludeAssigned,
    isPendingSlidesLoading,
    activePendingSlidesError,
    sourceAssignmentStrategy,
    pendingSlidesSelectionAction,
    disabled: disableInput,
    updateManifestDialogUrlState,
  });

  const changeWorkspaceSurface = (newSurface: WorkspaceSurface) => {
    setIsSelectSlidesMenuOpen(false);
    setHasChosenEntryIntent(true);
    if (newSurface === crossLabSearchSurface) {
      setLocalWorkspaceSurface(newSurface);
      return;
    }
    setLocalWorkspaceSurface(null);
    setManifestSurface(newSurface);
  };

  const openOrganization = () => {
    setLocalWorkspaceSurface(null);
    setManifestSurface('Manifest');
  };

  const isManifestDataLoading = isCasesLoading || isResolvingUploadedManifest || isHydratingManualManifestSlides;
  const shouldValidateManifestDraft = !isManifestDataLoading;
  const {
    applyCellValueChangedClass,
    editableManifestCaseIds,
    editableManifestSlideIds,
    onBulkEditSave,
    onCaseDataChange,
    onPageChange,
    onRowsPerPageChange,
    onShowOnlyErrorsSwitchChange,
    onSlideDataChange,
    pageCases,
    removeSelectedManifestSlidesFromManifest,
    selectedManifestSlidesCount,
    selectedManifestSlidesRemoveButtonText,
    totalCases,
    totalSlides,
  } = useManifestPlanEditing({
    currentDraftCases,
    baseManifestCases,
    baseResolvedManifestCases,
    fields,
    procedureFieldContext,
    shouldValidateManifestDraft,
    showOnlyErrors,
    page,
    rowsPerPage,
    isCaseLabelEditable,
    updateManifestDialogUrlState,
    setCaseManifestSelectedRows,
    setCaseManifestOmittedRows,
  });

  const validationErrorMessages = useMemo(
    () =>
      shouldValidateManifestDraft
        ? [
            ...collectManifestCaseLabelErrors(currentDraftCases, existingProcedures),
            ...collectExplicitNewCaseLabelErrors(currentDraftCases, existingProcedures, manifestCaseTargets),
            ...collectCaseSharedMetadataErrors(existingCaseAssignmentResolution.cases),
            ...collectManifestValidationErrors({
              cases: currentDraftCases,
              fields,
              procedureFieldContext,
              rowsPerPage,
            }),
          ]
        : [],
    [
      currentDraftCases,
      existingCaseAssignmentResolution,
      existingProcedures,
      fields,
      manifestCaseTargets,
      procedureFieldContext,
      rowsPerPage,
      shouldValidateManifestDraft,
    ]
  );

  const errorMessages = useMemo(
    () =>
      Array.from(
        new Set([...workflowResolution.errorMessages, ...manualManifestErrorMessages, ...validationErrorMessages])
      ),
    [manualManifestErrorMessages, workflowResolution.errorMessages, validationErrorMessages]
  );
  const planErrorMessages = useMemo(
    () => errorMessages.filter((message) => !uploadedErrorMessages.includes(message)),
    [errorMessages, uploadedErrorMessages]
  );

  const workbookProjection = useMemo(
    () =>
      isChoosingEntryIntent
        ? null
        : projectManifestWorkbookPlan({
            cases: concat(currentDraftCases, uploadedNotIngestedSlides),
            sourceCases: concat(editableExistingAndMockCases, uploadedManifestCases, manualManifestCases),
            existingCases: existingProcedures,
            targetsBySlideId: getManifestCaseTargets({
              cases: currentDraftCases,
              existingProcedures,
              assignmentsBySlideId: manifestCaseTargets,
            }),
            procedureFieldContext,
          }),
    [
      currentDraftCases,
      editableExistingAndMockCases,
      existingProcedures,
      isChoosingEntryIntent,
      manualManifestCases,
      manifestCaseTargets,
      procedureFieldContext,
      uploadedManifestCases,
      uploadedNotIngestedSlides,
    ]
  );
  const { downloadCurrentPlan, dismissWorkbookExportError, isExportingWorkbook, workbookExportError } =
    useManifestWorkbookExport({
      loading: destinationSourcesLoading,
      authorized: canIncludeAssigned,
      open,
      canSwitchLabs,
      currentPlanSignature,
      executionMode,
      existingProcedures,
      exportedPlanSignature,
      labId,
      procedureFieldContext,
      projection: workbookProjection,
      studyId,
    });

  const hasUploadedManifest =
    !isEmpty(uploadedManifestCases) ||
    !isEmpty(uploadedNotIngestedSlides) ||
    !isEmpty(uploadedWarningMessages) ||
    !isEmpty(uploadedErrorMessages);
  const hasResettableManifestChanges = !isEmpty(casePatchesById) || !isEmpty(slidePatchesById);

  const resetManifestChanges = useCallback(async () => {
    if (!hasResettableManifestChanges) {
      return;
    }

    const confirmed = await confirmWithModal({
      title: 'Reset Slide Organization Changes',
      text: (
        <>
          <span>
            This will discard unsaved edits made while reviewing slide organization and restore case and slide fields to
            their current values.
          </span>
          <br />
          <br />
          <span>
            This does not remove uploaded slides manifest rows, slides already added to the organization, or other
            composition changes.
          </span>
        </>
      ),
      confirmButtonProps: { title: 'Reset Changes' },
      cancelButtonProps: { title: 'Cancel' },
    });

    if (confirmed) {
      updateManifestDialogUrlState({ casePatchesById: {}, slidePatchesById: {} });
    }
  }, [confirmWithModal, hasResettableManifestChanges, updateManifestDialogUrlState]);

  const onExecutionModeChange = (event: ChangeEvent<HTMLInputElement>) => {
    invalidateUpload();
    setCaseOrganizationReviewOpen(false);
    updateManifestDialogUrlState(({ overrideCases: _previousOverride, ...previousState }) => ({
      ...previousState,
      executionMode: event.target.value as CaseOrganizationExecutionMode,
    }));
  };
  const runDisabledReason = useMemo(() => {
    if (!isExecutionModeValid) {
      return 'Choose a supported Existing case handling option before reviewing changes.';
    }

    if (!canIncludeAssigned) {
      return 'You do not have permission to apply case assignments.';
    }

    if (isCasesError) {
      return 'Could not load all study cases. Please contact support.';
    }

    if (createProceduresIsLoading) {
      return 'Wait for the current slide organization to finish.';
    }

    if (!isCasesLoading && existingCaseAssignmentResolution.missingTargetCount > 0) {
      const count = existingCaseAssignmentResolution.missingTargetCount;
      return count === 1 ? '1 slide still needs a target case.' : `${count} slides still need a target case.`;
    }

    if (!isEmpty(errorMessages)) {
      return 'Resolve the slide organization errors shown above to review changes.';
    }

    if (showOnlyErrors) {
      return 'Turn off Show only errors to review changes.';
    }

    if (isHydratingManualManifestSlides) {
      return 'Wait for slide metadata to finish loading.';
    }

    if (isManifestDataLoading) {
      return 'Wait for study data to finish loading.';
    }

    if (isEmpty(currentDraftCases)) {
      return isEmpty(allMockCasesOfPendingSlides)
        ? 'Upload a slides manifest to review changes.'
        : 'Add slides or upload a slides manifest to review changes.';
    }

    if (caseOrganizationPlan.isNoOp) {
      return 'This slide organization has no changes to apply.';
    }

    return null;
  }, [
    isExecutionModeValid,
    canIncludeAssigned,
    isCasesError,
    createProceduresIsLoading,
    isCasesLoading,
    existingCaseAssignmentResolution.missingTargetCount,
    errorMessages,
    showOnlyErrors,
    isHydratingManualManifestSlides,
    isManifestDataLoading,
    currentDraftCases,
    allMockCasesOfPendingSlides,
    caseOrganizationPlan.isNoOp,
  ]);

  const isRunDisabled = Boolean(runDisabledReason);
  const targetCaseOptions = [
    { id: 'new' as const, label: 'New case' },
    ...map(
      filter(
        existingProcedures,
        (procedure) =>
          (!procedure.labId || procedure.labId === labId) && (!procedure.studyId || procedure.studyId === studyId)
      ),
      (procedure) => ({ id: procedure.id, label: procedure.label || 'Unnamed case' })
    ),
  ];
  const existingCaseAssignmentColumns: GridColDef[] = [
    {
      field: 'targetCaseAssignment',
      headerName: 'Target case',
      width: 260,
      sortable: false,
      renderCell: ({ id, row }) => {
        const { baseRowId, innerRowId } = unwoundRowMetadataFromId(String(id));
        const slideId = String(innerRowId);
        const target = has(manifestCaseTargets, [slideId]) ? manifestCaseTargets[slideId] : Number(baseRowId);
        return (
          <CaseManifestTargetCaseCell
            options={targetCaseOptions}
            target={target}
            slideLabel={row.originalFileName || slideId}
            disabled={disableInput || isCasesError || !canIncludeAssigned}
            loading={isCasesLoading}
            onChange={(nextTarget) => {
              setCaseOrganizationReviewOpen(false);
              updateManifestDialogUrlState((previousState) =>
                updateManifestCaseTarget({
                  previousState,
                  currentDraftCases,
                  slideId,
                  previousTarget: target,
                  target: nextTarget,
                })
              );
            }}
          />
        );
      },
    },
  ];
  const dialogLoadingStatusText = isHydratingManualManifestSlides
    ? 'Loading slide metadata for case assignment...'
    : null;
  const caseListLoadingIcon = isCasesLoading ? <CircularProgress color="inherit" size={16} /> : undefined;

  const selectSlidesControl = (
    <CaseManifestSlideSourceMenu
      anchorEl={selectSlidesAnchorEl}
      entries={selectionSourceEntries}
      isChoosingEntryIntent={isChoosingEntryIntent}
      label={selectSlidesButtonLabel}
      onChange={changeWorkspaceSurface}
      onOpenChange={setIsSelectSlidesMenuOpen}
      open={isSelectSlidesMenuOpen}
      selectedSurface={activeWorkspaceSurface === 'Manifest' ? defaultAddSlidesSurface : activeWorkspaceSurface}
      setAnchorEl={setSelectSlidesAnchorEl}
    />
  );
  const unassignedStudySlideCount = flatMap(allMockCasesOfStudyPendingSlides, 'slides').length;
  const canReviewUnassigned =
    canReadStudySource &&
    canIncludeAssigned &&
    !isStudyPendingSlidesLoading &&
    !studyPendingSlidesError &&
    unassignedStudySlideCount > 0;
  const reviewUnassignedSlides = (additionalState: Partial<ManifestDialogUrlState> = {}) => {
    updateManifestDialogUrlState({ manualManifestCases: allMockCasesOfStudyPendingSlides, ...additionalState });
    setHasChosenEntryIntent(true);
    openOrganization();
  };
  const { reviewUnassignedSlidesWhenAvailable } = manifestState;
  useEffect(() => {
    if (!reviewUnassignedSlidesWhenAvailable || !open || isStudyPendingSlidesLoading) return;
    if (canReviewUnassigned) {
      reviewUnassignedSlides({ reviewUnassignedSlidesWhenAvailable: undefined });
    } else {
      updateManifestDialogUrlState({ reviewUnassignedSlidesWhenAvailable: undefined });
    }
    // Not exhaustive: the handlers are recreated every render and must not re-trigger it; the flag is cleared when consumed.
  }, [reviewUnassignedSlidesWhenAvailable, open, isStudyPendingSlidesLoading, canReviewUnassigned]);
  const entryContext = [currentLab?.name, find(procedureFieldContext.studies, { id: studyId })?.name]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Dialog
        open={open}
        fullScreen={!isChoosingEntryIntent}
        fullWidth={isChoosingEntryIntent}
        maxWidth={false}
        PaperProps={isChoosingEntryIntent ? { sx: { maxWidth: 1000 } } : undefined}
        onClose={isChoosingEntryIntent ? onCloseDialog : undefined}
        aria-labelledby="case-organization-title"
        scroll="paper"
      >
        <DialogChromeHeader
          title={dialogTitle}
          titleId="case-organization-title"
          subtitle={isChoosingEntryIntent ? entryContext : undefined}
          onClose={onCloseDialog}
          sx={{ '& h1': { fontSize: 24 } }}
        />
        {!isChoosingEntryIntent && (
          <ManifestDraftStatus
            blockingErrorCount={errorMessages.length}
            caseCount={currentDraftCases.length}
            isOrganizationView={isOrganizationView}
            onReviewUnresolvedRows={() => changeWorkspaceSurface('Not Ingested Slides')}
            executionMode={isExecutionModeValid ? executionMode : undefined}
            slideCount={flatMap(currentDraftCases, 'slides').length}
            unresolvedRowCount={notIngestedSlidesCount}
            warningCount={warningMessages.length}
          />
        )}
        {destinationTransfer &&
          destinationTransfer.destination.labId === labId &&
          destinationTransfer.destination.studyId === studyId && (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => void returnToOriginalPlan()}
                  disabled={isReturningToOriginal || isResolvingUploadedManifest || createProceduresIsLoading}
                >
                  Return to original plan
                </Button>
              }
            >
              {canIncludeAssigned
                ? `Case plan destination: ${destinationTransfer.destination.studyName} (${destinationTransfer.destination.labName}).`
                : 'The original case plan is preserved.'}
            </Alert>
          )}
        {destinationError?.scope === uploadScope && <Alert severity="error">{destinationError.message}</Alert>}
        {!isChoosingEntryIntent && (
          <CaseManifestHandling
            disabled={!canIncludeAssigned || createProceduresIsLoading || isResolvingUploadedManifest}
            executionMode={executionMode}
            expanded={isHandlingExpanded ?? false}
            isExecutionModeValid={isExecutionModeValid}
            onChange={onExecutionModeChange}
            onExpandedChange={setIsHandlingExpanded}
          />
        )}
        <DialogContent dividers>
          {isChoosingEntryIntent ? (
            <CaseManifestEntryChoices
              canReviewUnassigned={canReviewUnassigned}
              caseListLoadingIcon={caseListLoadingIcon}
              existingCasesDisabled={
                !canIncludeAssigned || isCasesLoading || isCasesError || isEmpty(existingProcedures)
              }
              isCasesError={isCasesError}
              isCasesLoading={isCasesLoading}
              onReviewExisting={() => {
                updateManifestDialogUrlState({ manualManifestCases: existingProcedures });
                setHasChosenEntryIntent(true);
                openOrganization();
              }}
              onReviewUnassigned={() => reviewUnassignedSlides()}
              selectSlidesControl={selectSlidesControl}
              studySourceStatus={
                canReadStudySource
                  ? isStudyPendingSlidesLoading
                    ? 'loading'
                    : studyPendingSlidesError
                    ? 'error'
                    : null
                  : null
              }
              unassignedStudySlideCount={unassignedStudySlideCount}
            />
          ) : (
            <>
              <CaseManifestPlanToolbar
                fileMenuProps={{
                  canDownload: canIncludeAssigned,
                  canUpload: canIncludeAssigned,
                  caseCount: currentDraftCases.length,
                  executionMode: isExecutionModeValid ? executionMode : null,
                  exporting: isExportingWorkbook,
                  loading: destinationSourcesLoading,
                  closeMenuRef: closeFileMenuRef,
                  onDownloadPlan: () => void downloadCurrentPlan(),
                  onUploadClick: () => manifestInputRef.current?.click(),
                  resolving: isResolvingUploadedManifest,
                  slideCount: flatMap(currentDraftCases, 'slides').length,
                }}
                manifestInputRef={manifestInputRef}
                onFileChange={handleFileChange}
                selectSlidesControl={selectSlidesControl}
                uploadDisabled={!canIncludeAssigned || isResolvingUploadedManifest}
              />
              <CaseManifestFileStatus
                canFixInCasePlan={Boolean(pendingCasePlanFix)}
                canMatchContext={Boolean(
                  activeWorkbookContext?.result.status === 'valid' &&
                    ((activeWorkbookContext.result.context.studyId === studyId &&
                      activeWorkbookContext.result.context.labId === labId) ||
                      (destinationLookup.data && !destinationLookup.isFetching && !destinationLookup.isError))
                )}
                contextFeedback={activeWorkbookContext?.result}
                destination={destinationLookup.data}
                destinationError={
                  foreignContext && destinationLookup.isError
                    ? 'The destination is unavailable or you do not have access to organize its slides.'
                    : undefined
                }
                disabled={disableInput || !canIncludeAssigned}
                downloadError={workbookExportError}
                errors={uploadedErrorMessages}
                executionMode={isExecutionModeValid ? executionMode : null}
                exporting={isExportingWorkbook}
                onConfirmOverwrite={() => {
                  if (activePendingOverwrite && overwriteAccepted)
                    void importManifestFile(activePendingOverwrite.file, false, true);
                }}
                onDismiss={() => {
                  dismissFileImport();
                  if (
                    showOnlyErrors &&
                    !planErrorMessages.length &&
                    !existingCaseAssignmentResolution.missingTargetCount &&
                    !workbookExportError
                  )
                    updateManifestDialogUrlState({ showOnlyErrors: false });
                }}
                onDismissDownloadError={() => {
                  dismissWorkbookExportError();
                  if (
                    showOnlyErrors &&
                    !planErrorMessages.length &&
                    !existingCaseAssignmentResolution.missingTargetCount &&
                    !uploadedErrorMessages.length
                  )
                    updateManifestDialogUrlState({ showOnlyErrors: false });
                }}
                onFixInCasePlan={() => {
                  if (pendingCasePlanFix) acceptResolvedManifestUpload(pendingCasePlanFix);
                }}
                onKeepContext={() => reconcileWorkbookContext(false)}
                onMatchContext={() => reconcileWorkbookContext(true)}
                onOverwriteAccepted={setOverwriteAccepted}
                onRetryDestination={() => void destinationLookup.refetch()}
                overwriteAccepted={Boolean(activePendingOverwrite) && overwriteAccepted}
                pendingOverwrite={Boolean(activePendingOverwrite)}
                resolving={isResolvingUploadedManifest || Boolean(foreignContext && destinationLookup.isFetching)}
              />

              <CaseManifestWorkspace
                activeSurface={activeWorkspaceSurface}
                loadingStatusText={dialogLoadingStatusText}
                directAddSearchProps={{
                  availablePendingSlides: allLabPendingSlides || [],
                  canSearchAcrossLabs: canAddSlidesAcrossLabs,
                  onAdd: addDirectSlidesToManifest,
                  targetStudyId: studyId,
                }}
                notIngestedSlides={notIngestedSlides}
                sourcePanelProps={sourcePanelProps}
                planTableProps={{
                  labId,
                  editableCaseIds: editableManifestCaseIds,
                  editableSlideIds: editableManifestSlideIds,
                  hasDraftCases: !isEmpty(currentDraftCases),
                  isCaseLabelEditable,
                  onRemoveSelectedSlides: removeSelectedManifestSlidesFromManifest,
                  onShowOnlyErrorsChange: onShowOnlyErrorsSwitchChange,
                  removeButtonText: selectedManifestSlidesRemoveButtonText,
                  selectedSlideCount: selectedManifestSlidesCount,
                  showOnlyErrors,
                  gridProps: {
                    casesInPage: pageCases,
                    totalSlides,
                    totalCases,
                    isLoading: isResolvingUploadedManifest || isHydratingManualManifestSlides,
                    additionalSlideColumns: existingCaseAssignmentColumns,
                    onCaseDataChange,
                    onSlideDataChange,
                    applyCellValueChangedClass,
                    onBulkEditSave,
                    onPageChange,
                    onRowsPerPageChange,
                    forcePage: page,
                    forcePageSize: rowsPerPage,
                  },
                }}
                errorMessages={planErrorMessages}
                warningMessages={warningMessages}
              />
            </>
          )}
        </DialogContent>
        <CaseManifestDialogFooter
          caseListLoadingIcon={caseListLoadingIcon}
          hasResettableManifestChanges={hasResettableManifestChanges}
          hasUploadedManifest={hasUploadedManifest}
          isCasesLoading={isCasesLoading}
          isChoosingEntryIntent={isChoosingEntryIntent}
          isOrganizationView={isOrganizationView}
          onClose={onCloseDialog}
          onOpenOrganization={openOrganization}
          onResetChanges={resetManifestChanges}
          onReviewChanges={() => setCaseOrganizationReviewOpen(true)}
          runDisabledReason={runDisabledReason}
        />
      </Dialog>

      {activeDestinationConfirmation && (
        <ConfirmationModal
          title="Switch case plan destination"
          text={`Switch to ${activeDestinationConfirmation.destination.studyName} in ${activeDestinationConfirmation.destination.labName}? Your original unsaved plan will be preserved for return. No saved assignments will change.`}
          confirmButtonProps={{ title: 'Switch destination', color: 'primary' }}
          cancelButtonProps={{ color: 'inherit' }}
          onConfirm={() => void switchManifestDestination()}
          onCancel={dismissDestinationConfirmation}
        />
      )}
      <StudyManifestInProgressDialog
        open={runningDialogOpen}
        completedBatches={completedBatchCount}
        totalBatches={totalBatches}
        onClose={onCloseDialog}
        setRunningDialogOpen={setRunningDialogOpen}
        failedBatches={failedCaseBatchCount}
        overrideFailed={overrideFailed}
        failedSchedulingCount={failedSchedulingCount}
        onRetryFailedBatches={retryFailedCaseBatches}
      />
      <CaseOrganizationConfirmationDialog
        key={`${executionMode}:${studyId}`}
        open={caseOrganizationReviewOpen && !isRunDisabled}
        onClose={() => setCaseOrganizationReviewOpen(false)}
        onApplyCaseOrganization={applyCaseOrganization}
        plan={caseOrganizationPlan}
      />
    </>
  );
};

export default CaseManifestDialog;
